/**
 * Org-scoped (gateway-scoped) skill catalog API hooks.
 *
 * Distinct from `useSkillsQuery` in api/skills, which targets the
 * agent-scoped enable/disable surface. This module wraps the org-level
 * `/api/skills*` routes.
 */

import type { SkillCatalogDetail, SkillCatalogEntry } from "@lifter/hub-ui/browser-shared";
import { hubQueryKeys, notifyUnauthorized, performHubRequest } from "@lifter/hub-ui/browser-shared";
import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext } from "react";
import { z } from "zod";

export interface SkillCatalogListData {
  bundled: SkillCatalogEntry[];
  catalog: SkillCatalogEntry[];
}

export interface CompanionFilePayload {
  path: string;
  content: string;
  encoding: "utf8" | "base64";
}

export interface CreateSkillInput {
  title: string;
  description: string;
  markdown: string;
  companionFiles?: CompanionFilePayload[];
}

/** A catalog update carries identity and SKILL.md only; companions are immediate. */
export interface UpdateSkillInput {
  title: string;
  description: string;
  markdown: string;
}

/**
 * Exported for tests. The org-skills routes (`/api/skills*`) speak the standard
 * Hub `{ ok, data }` envelope, so this now routes through the shared
 * {@link performHubRequest} wrapper — same fetch + `NETWORK_ERROR` wrap + global
 * 401 handling + envelope unwrap as every other Hub surface. Content-type is set
 * for JSON bodies; `z.unknown()` keeps the org-skill payloads unvalidated at the
 * client (the routes' own contract types are the source of truth).
 */
export async function fetchJson<T>(input: string, init?: RequestInit): Promise<T> {
  const headers = {
    ...(init?.headers ?? {}),
    ...(init?.body && !(init.body instanceof FormData)
      ? { "content-type": "application/json" }
      : {}),
  };
  return performHubRequest<T>(
    // Wrapped, never a bare `fetch` reference: invoking a detached `fetch`
    // throws "Illegal invocation" in Chromium (jsdom does not care, so unit
    // tests cannot catch a regression here).
    { fetchImpl: (...args) => fetch(...args), onUnauthorized: notifyUnauthorized },
    input,
    { ...init, headers },
    z.unknown() as z.ZodType<T>,
  );
}

export function useSkillCatalogQuery(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: hubQueryKeys.skillCatalog(),
    queryFn: () => fetchJson<SkillCatalogListData>("/api/skills"),
    enabled: options.enabled ?? true,
  });
}

export function useReadSkillQuery(skillId: string | null) {
  return useQuery({
    queryKey: hubQueryKeys.skillCatalogEntry(skillId ?? ""),
    queryFn: () =>
      fetchJson<{ skill: SkillCatalogDetail }>(`/api/skills/${encodeURIComponent(skillId ?? "")}`),
    enabled: Boolean(skillId),
  });
}

export function useCreateSkillMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSkillInput) =>
      fetchJson<{ skill: SkillCatalogDetail }>("/api/skills", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: hubQueryKeys.skillCatalog() });
    },
  });
}

export function useUpdateSkillMutation(skillId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateSkillInput) =>
      fetchJson<{ skill: SkillCatalogDetail }>(`/api/skills/${encodeURIComponent(skillId)}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: hubQueryKeys.skillCatalog() });
      qc.invalidateQueries({ queryKey: hubQueryKeys.skillCatalogEntry(skillId) });
    },
  });
}

export function useDeleteSkillMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (skillId: string) =>
      fetchJson<{ ok: true }>(`/api/skills/${encodeURIComponent(skillId)}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: hubQueryKeys.skillCatalog() });
    },
  });
}

export function useCloneSkillMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bundledId: string) =>
      fetchJson<{ skill: SkillCatalogDetail }>(
        `/api/skills/clone/${encodeURIComponent(bundledId)}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: hubQueryKeys.skillCatalog() });
    },
  });
}

export function useSkillFilesPending(skillId: string) {
  return useIsMutating({ mutationKey: hubQueryKeys.skillCatalogEntry(skillId) }) > 0;
}

/**
 * Companion mutations persist immediately. The editor merges their returned
 * detail into its baseline while keeping unsaved content; catalog invalidation
 * also refreshes cached detail queries under the same key prefix. Their shared
 * scope serializes responses so an older detail cannot undo a later file move.
 */
export function useUploadSkillFilesMutation(skillId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: hubQueryKeys.skillCatalogEntry(skillId),
    scope: { id: `skill-files:${skillId}` },
    mutationFn: (files: CompanionFilePayload[]) =>
      fetchJson<{ skill: SkillCatalogDetail }>(
        `/api/skills/${encodeURIComponent(skillId)}/upload`,
        { method: "POST", body: JSON.stringify({ files }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: hubQueryKeys.skillCatalog() });
    },
  });
}

export function useDeleteSkillFileMutation(skillId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: hubQueryKeys.skillCatalogEntry(skillId),
    scope: { id: `skill-files:${skillId}` },
    mutationFn: (path: string) =>
      fetchJson<{ skill: SkillCatalogDetail }>(
        // Encode each segment but keep the `/` separators so the Gateway's
        // `:path{.+}` route captures the nested companion path intact.
        `/api/skills/${encodeURIComponent(skillId)}/files/${path
          .split("/")
          .map(encodeURIComponent)
          .join("/")}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: hubQueryKeys.skillCatalog() });
    },
  });
}

export function useMoveSkillFileMutation(skillId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: hubQueryKeys.skillCatalogEntry(skillId),
    scope: { id: `skill-files:${skillId}` },
    mutationFn: (move: { from: string; to: string }) =>
      fetchJson<{ skill: SkillCatalogDetail }>(
        `/api/skills/${encodeURIComponent(skillId)}/files/move`,
        { method: "POST", body: JSON.stringify(move) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: hubQueryKeys.skillCatalog() });
    },
  });
}

export interface SkillCatalogContextValue {
  data: SkillCatalogListData | undefined;
  dataUpdatedAt?: number;
  isLoading: boolean;
  isError: boolean;
  isFetching?: boolean;
  refetch?(): unknown;
}

/**
 * Context for tests / stories to inject fake catalog data without
 * spinning up React Query.
 */
export const SkillCatalogContext = createContext<SkillCatalogContextValue | null>(null);
